import { useState, useEffect, useRef } from "react";
import { Quote, ChevronLeft, ChevronRight, Star } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";

export interface TestimonialItem {
  quote: string;
  author: string;
  role: string;
  company: string;
  rating?: number;
}

interface TestimonialCarouselProps {
  testimonials: TestimonialItem[];
  autoplayDelay?: number;
}

const TestimonialCarousel = ({ testimonials, autoplayDelay = 5000 }: TestimonialCarouselProps) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  const scrollPrev = () => setSelectedIndex((i) => (i === 0 ? testimonials.length - 1 : i - 1));
  const scrollNext = () => setSelectedIndex((i) => (i === testimonials.length - 1 ? 0 : i + 1));

  // Intersection observer for fade-in
  useEffect(() => {
    const element = sectionRef.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  // Simple autoplay
  useEffect(() => {
    if (isPaused) return;
    const interval = setInterval(scrollNext, autoplayDelay);
    return () => clearInterval(interval);
  }, [isPaused, autoplayDelay]);

  const accentColors = [
    "from-primary/20 to-primary/5",
    "from-blue-500/20 to-blue-500/5",
    "from-purple-500/20 to-purple-500/5",
  ];

  return (
    <section 
      ref={sectionRef}
      className={cn(
        "py-32 relative overflow-hidden transition-all duration-700",
        isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
      )}
    >
      {/* Background pattern */}
      <div className="absolute inset-0 bg-dot-pattern opacity-20" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[400px] rounded-full bg-primary/5 blur-[60px]" />
      
      <div className="container mx-auto px-6 relative z-10">
        <div className="text-center mb-16">
          <p className="text-primary font-mono text-sm tracking-wider mb-4">
            TESTIMONIALS
          </p>
          <h2 className="text-3xl md:text-5xl font-bold mb-4">
            What Clients Say
          </h2>
          <p className="text-muted-foreground max-w-xl mx-auto">
            Real feedback from founders and executives I've worked with
          </p>
        </div>

        {/* Simple fade carousel */}
        <div 
          className="relative max-w-4xl mx-auto"
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
        >
          {testimonials.map((testimonial, index) => (
            <div
              key={index}
              className={cn(
                "transition-all duration-500 ease-out",
                selectedIndex === index 
                  ? "opacity-100 translate-x-0" 
                  : "opacity-0 absolute inset-0 translate-x-8 pointer-events-none"
              )}
            >
              <div
                className={cn(
                  "relative p-8 md:p-12 rounded-2xl border border-border bg-gradient-to-br",
                  accentColors[index % accentColors.length]
                )}
              >
                {/* Large decorative quote */}
                <Quote 
                  className="absolute top-6 left-6 text-primary/10" 
                  size={80} 
                  strokeWidth={1}
                />
                
                {/* Rating stars */}
                {testimonial.rating && (
                  <div className="flex gap-1 mb-6 relative z-10">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <Star
                        key={i}
                        size={18}
                        className={cn(
                          i < testimonial.rating! 
                            ? "text-primary fill-primary" 
                            : "text-muted-foreground/30"
                        )}
                      />
                    ))}
                  </div>
                )}

                {/* Quote text */}
                <blockquote className="relative z-10 text-lg md:text-2xl font-medium leading-relaxed mb-8">
                  "{testimonial.quote}"
                </blockquote>

                {/* Author */}
                <div className="relative z-10 flex items-center gap-4">
                  <Avatar className="h-14 w-14 border-2 border-primary/30">
                    <AvatarFallback className="bg-primary/10 text-primary font-semibold text-lg">
                      {testimonial.author.charAt(0)}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="font-semibold text-lg">{testimonial.author}</p>
                    <p className="text-sm text-muted-foreground">
                      {testimonial.role}, {testimonial.company}
                    </p>
                  </div>
                </div>

                {/* Decorative corner elements */}
                <div className="absolute top-0 right-0 w-20 h-20 border-r-2 border-t-2 border-primary/10 rounded-tr-2xl" />
                <div className="absolute bottom-0 left-0 w-20 h-20 border-l-2 border-b-2 border-primary/10 rounded-bl-2xl" />
              </div>
            </div>
          ))}

          {/* Navigation */}
          <div className="flex items-center justify-center gap-4 mt-8">
            <Button
              variant="outline"
              size="icon"
              onClick={scrollPrev}
              className="rounded-full border-border hover:border-primary/50 hover:bg-primary/10"
              aria-label="Previous testimonial"
            >
              <ChevronLeft size={20} />
            </Button>
            
            {/* Dots */}
            <div className="flex gap-2" role="tablist" aria-label="Testimonial navigation">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setSelectedIndex(index)}
                  className={cn(
                    "w-2 h-2 rounded-full transition-all duration-300",
                    selectedIndex === index 
                      ? "w-8 bg-primary" 
                      : "bg-muted-foreground/30 hover:bg-muted-foreground/50"
                  )}
                  aria-label={`Go to testimonial ${index + 1}`}
                  aria-selected={selectedIndex === index}
                  role="tab"
                />
              ))}
            </div>

            <Button
              variant="outline"
              size="icon"
              onClick={scrollNext}
              className="rounded-full border-border hover:border-primary/50 hover:bg-primary/10"
              aria-label="Next testimonial"
            >
              <ChevronRight size={20} />
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TestimonialCarousel;
